import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import RadioEditor from './RadioEditor';
import CheckboxEditor from './CheckboxEditor';
import SelectEditor from './SelectEditor';
import DateEditor from './DateEditor';
import NumberEditor from './NumberEditor';
import BetweenEditor from './BetweenEditor';
import TextEditor from './TextEditor';

interface ValueEditorProps {
    ruleId: string;
    field?: QueryField;
    operator: string;
    value: QueryRule['value'];
    onChange: (value: QueryRule['value']) => void;
    compact?: boolean;
    // Forwarded to BetweenEditor: swaps min/max if entered in the wrong order. See BetweenEditor.
    betweenSwapIfInverted?: boolean;
}

const toStringValue = (value: QueryRule['value']): string => {
    if (value === null || value === undefined) return '';
    return Array.isArray(value) ? value.join(',') : String(value);
};

const toSelectValue = (value: QueryRule['value']): string | string[] => {
    if (value === null || value === undefined) return '';
    return Array.isArray(value) ? value : String(value);
};

// Fields whose values today amount to a single "is this subscribed" flag: rendered as the same
// tri-state toggle (subscribed / unsubscribed / no response) as the opt-in fields, instead of the
// generic per-value checkbox list, to match how consent is represented everywhere else.
const SUBSCRIPTION_TRISTATE_FIELD_NAMES = new Set(['CUSTOMER_SUBSCRIPTIONS_TYPE']);

function ValueEditor({ ruleId, field, operator, value, onChange, compact = false, betweenSwapIfInverted }: ValueEditorProps) {
    const { t } = useLanguage();

    if (!field) {
        return (
            <TextEditor
                value={toStringValue(value)}
                onChange={onChange}
                placeholder={t('pages.clients.queryBuilder.simple.selectFieldFirstPlaceholder')}
            />
        );
    }

    const { valueEditorType, inputType, values } = field;

    if (operator === 'between') {
        return (
            <BetweenEditor
                value={toStringValue(value)}
                inputType={inputType}
                onChange={onChange}
                swapIfInverted={betweenSwapIfInverted}
            />
        );
    }

    if ((operator === 'in' || operator === 'not in') &&
        (valueEditorType === 'select' || valueEditorType === 'multiselect')) {
        return (
            <SelectEditor
                ruleId={ruleId}
                value={toSelectValue(value)}
                values={values || []}
                isMultiSelect={true}
                compact={compact}
                onChange={onChange}
            />
        );
    }

    if (valueEditorType === 'checkbox') {
        const subscriptionValues = values || [];
        // Each entry in `values` is its own subscription type (e.g. "Consentement", "Newsletter", ...),
        // each independently toggled subscribed / unsubscribed / no response — rendered as one
        // tri-state toggle per entry, instead of the generic per-value checkbox list, to match how
        // consent is represented everywhere else.
        const isSubscriptionTristate = SUBSCRIPTION_TRISTATE_FIELD_NAMES.has(field.name) && subscriptionValues.length > 0;
        return (
            <CheckboxEditor
                ruleId={ruleId}
                value={value ?? null}
                values={isSubscriptionTristate ? [] : subscriptionValues}
                onChange={onChange}
                compact={compact}
                subscriptionOptions={isSubscriptionTristate ? subscriptionValues : undefined}
                booleanLabels={isSubscriptionTristate ? {
                    accept: t('pages.clients.queryBuilder.simple.subscription.subscribed'),
                    refuse: t('pages.clients.queryBuilder.simple.subscription.unsubscribed'),
                    noPreference: t('pages.clients.queryBuilder.simple.subscription.noResponse'),
                } : undefined}
            />
        );
    }

    if (valueEditorType === 'select' || valueEditorType === 'multiselect') {
        return (
            <SelectEditor
                ruleId={ruleId}
                value={toSelectValue(value)}
                values={values || []}
                isMultiSelect={compact || valueEditorType === 'multiselect'}
                compact={compact}
                onChange={onChange}
            />
        );
    }

    if (valueEditorType === 'radio' && values && values.length > 0) {
        return (
            <RadioEditor
                ruleId={ruleId}
                value={toStringValue(value)}
                values={values}
                onChange={onChange}
            />
        );
    }

    if (inputType === 'number') {
        return (
            <NumberEditor
                value={toStringValue(value)}
                onChange={onChange}
            />
        );
    }

    if (inputType === 'date') {
        return (
            <DateEditor
                value={toStringValue(value)}
                onChange={onChange}
            />
        );
    }

    return (
        <TextEditor
            value={toStringValue(value)}
            onChange={onChange}
        />
    );
}

export default ValueEditor;
